import { useCallback, useEffect, useRef } from "react"

import { isMobile } from "@utils/V2/screen"

/**
 * Custom hook uses IntersectionObserver to fade up animate an element
 * if the element is scrolled into view by adding animation classes and
 * animation delay styling based on the nth index of the element.
 *
 * This version uses useCallback instead of a ref parameter and useEffect.
 * It directly manipulates the node passed to it, similar to how measuredRef works.
 *
 * @returns object containing:
 * - `animation`: A callback function that should be passed to the `ref` attribute of a React element to handle scroll animations.
 * - `ref`: A React ref object for external use, allowing direct access to the DOM element being animated.
 */
export const useScrollAnimateSelf = <T extends HTMLElement>(
  shouldAnimate = true,
  /**
   * Used to determine the threshold for the IntersectionObserver on mobile
   * when this component is a child of a SliderSection
   */
  isSliderChild = false
) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const externalRef = useRef<T | null>(null)

  const callback = useCallback(
    (node: T | null) => {
      if (!shouldAnimate || !node) return

      externalRef.current = node

      // If the element is above the fold, we make it visible immediately
      const rect = node.getBoundingClientRect()
      const isAboveTheFold = rect.top < window.innerHeight
      if (isAboveTheFold) {
        node.classList.add("testid-scroll-animate")
        return
      }

      node.classList.add("testid-scroll-animate", "opacity-0")

      const handleIntersect = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const childIndex = node.parentElement
              ? Array.from(node.parentElement.children).indexOf(node)
              : 0

            if (!isMobile()) {
              node.style.animationDelay = `${childIndex * 200}ms`
            }

            node.classList.add("animate-fadeUp")

            if (observerRef.current) {
              observerRef.current.disconnect()
            }
          }
        })
      }

      const options = {
        root: null,
        rootMargin: "0%",
        threshold: isMobile() && isSliderChild ? 0 : 0.3,
      }

      if (observerRef.current) observerRef.current.disconnect() // Disconnect existing observer if any
      observerRef.current = new IntersectionObserver(handleIntersect, options)
      observerRef.current.observe(node)
    },
    [isSliderChild, shouldAnimate]
  )

  // Cleanup observer when the component unmounts
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return { animationRefCallback: callback, ref: externalRef }
}
